import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth, UserRole } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
}

const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { user, loading, userRole, tenantId, signingOut } = useAuth();
  const { tenantSlug: urlTenantSlug } = useParams<{ tenantSlug?: string }>();
  const [tenantSlug, setTenantSlug] = useState<string | null>(urlTenantSlug || null);
  // Note: we intentionally do NOT block render on slug fetch. The slug is only
  // used as a redirect fallback when the user is signed out, so fetching it in
  // the background avoids showing a full-screen loader on every navigation
  // (which caused pages to appear to "load twice" before the lazy chunk).
  const [slugLoading] = useState(false);

  // Fetch tenant slug in the background if not in URL but we have tenantId
  useEffect(() => {
    if (urlTenantSlug || !tenantId) return;

    const fetchSlug = async () => {
      try {
        const { data } = await supabase
          .from('tenants')
          .select('slug')
          .eq('id', tenantId)
          .maybeSingle();
        setTenantSlug(data?.slug || null);
      } catch (error) {
        console.error('Error fetching tenant slug:', error);
      }
    };
    fetchSlug();
  }, [urlTenantSlug, tenantId]);

  // Show loading during sign-out to prevent stale UI with old permissions
  if (loading || slugLoading || signingOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{signingOut ? 'Signing out...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to tenant-specific auth page if we know the tenant
    if (tenantSlug) {
      return <Navigate to={`/auth/${tenantSlug}`} replace />;
    }
    return <Navigate to="/auth" replace />;
  }

  // If specific roles are required, check them
  if (allowedRoles && allowedRoles.length > 0) {
    if (!userRole || !allowedRoles.includes(userRole)) {
      // Redirect to tenant-scoped dashboard if available
      if (tenantSlug) {
        return <Navigate to={`/${tenantSlug}/dashboard`} replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
